import React from "react";
import useMeta from "@/hooks/useMeta";

const PORTRAIT_1 = "/api/static/assets/9550bcdb_v0tmuuct_Photoroom_20251102_153218.webp";
const PORTRAIT_2 = "/api/static/assets/a545c193_qrl4pydg_IMG_2928.webp";
const SEMINAR_1 = "/api/static/assets/6d945d7d_p5vr2u8j_000015202368.jpg";
// Rotated 90° clockwise from the original Barcelona 2015 shot to stand upright.
const FRAMEWORK = "/faculty/framework-upright.webp";
const CONFERENCE_1 = "https://globalleadersolutions.com/wp-content/uploads/2026/02/Image-3_IN-22764-2048x1536.jpg";

const KEYNOTE_PHOTOS = [
  // Row 1
  { src: "/api/static/assets/b46eb8a6_dqp7z4hy_banner44.jpg",
    alt: "Keynote — Rochester Conference, Manila, Philippines",
    caption: "Keynote: Rochester Conference — Manila, Philippines, May 2021" },
  { src: "/api/static/assets/9eabcae4_wsk71m8u_GGA.jpg",
    alt: "Global Goodwill Ambassadors Humanitarian honor" },
  { src: "/api/static/assets/d26aaae2_z4fayamt_WhatsApp_Image_2023-07-10_at_11.00.57_AM.jpeg",
    alt: "Keynote — Pakistan Diplomatic College, Islamabad",
    caption: "Keynote: Pakistan Diplomatic College — Islamabad, Pakistan, July 2023" },

  // Row 2
  { src: "/api/static/assets/abcb8e59_tg1w1x4n_Barcelona_2_1015.webp",
    alt: "17th International Leadership Conference — Forgiveness as a Leadership Competence",
    caption: "Barcelona ILA 2016" },
  { src: "/api/static/assets/fcaa518c_u2vwi3g0_The_Hague.jpg",
    alt: "The Hague keynote address",
    caption: "The Hague 2014" },
  { src: "/api/static/assets/b0577272_fg22tbv9_ICQ_Global_-_Global_DISC_Accredited_Partner_-_2022-08-04.jpg",
    alt: "ICQ Global DISC — Accredited Partner",
    caption: "Global DISC™ Accredited Partner" },

  // Row 3 (middle Strategic Framework tile removed)
  { src: "/api/static/assets/0f6819df_gjy4yxbr_000018345608.jpg",
    alt: "Ho Chi Minh Leadership & Public Policy keynote",
    caption: "Ho Chi Minh Leadership & Public Policy, Oct 2019" },
  { src: "/api/static/assets/87171a29_brtd7ce1_IMG_20170217_164446.webp",
    alt: "Peace Leadership Conference — India",
    caption: "Peace Leadership Conference — India, 2017" },
  { src: "/api/static/assets/42d5d73e_hu4cw399_1615401362753.jfif",
    alt: "Digital Defence — Next Generation Cyber Minds Keynote (March 2021)" },

  // Row 4
  { src: "/api/static/assets/2e6d0ce1_za79isch_1617094634942.jpg",
    alt: "Digital Defence Cyber Minds Keynote Speaker programme" },
  { src: "/api/static/assets/574722db_ad8b7cso_Honours-14.jpg",
    alt: "Marquis Who's Who in America — Listed",
    caption: "Listed — Marquis Who's Who in America" },
  { src: "/api/static/assets/28eea8b7_ts5tfdk0_pmi_chicagoland_chapter_horizontal_logo_full_color_rgb_882px_w_72ppi_Logo.jpg",
    alt: "PMI Chicagoland Chapter",
    caption: "Keynote: PMI Chicagoland Conference, 2024" },
];

const CREDENTIALS = [
  "Doctorate of Global Leadership — Indiana Institute of Technology",
  "Master of Diplomacy in International Conflict Management — Norwich University",
  "Adjunct Instructor at Global Professional Military Education Center — Air Force Command and Staff College, Yale University, Norwich University, and Federal Executive Leadership Institute",
  "Certified in Ken Blanchard Situational Leadership, Global DISC, and Emotional Intelligence 2.0 from the College of Executive Coaching",
];

const BOOKS = [
  { title: "Business to Diplomacy: The Complexity of Leadership", year: 2024 },
  { title: "Chaos is a Gift: Leading Oneself in Uncertain and Complex Environments", year: 2020 },
  { title: "Peace Leadership: Self-Transformation toward Peace", year: 2019 },
  { title: "Global Leadership Initiatives for Conflict Resolution and Peacebuilding", year: 2018 },
];

const Faculty = () => {
  useMeta({
    title: "Faculty & Advisory Board — Global Leader Solutions",
    description: "Meet Dr. Andrew H. Campbell and the faculty of the Global Leadership Education and Training Institute (GLETI) — thought leaders in character-based leadership.",
    canonical: "https://globalleadersolutions.com/faculty",
  });
  return (
  <div data-testid="faculty-page" className="bg-white">
    {/* HERO / ABOUT */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <img src={PORTRAIT_1} alt="Dr. Andrew Campbell portrait" data-testid="faculty-portrait-1"
            className="w-full h-auto rounded-sm shadow-md object-cover" />
        </div>
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Faculty</div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold text-[#2D2628] mb-4">
            About Dr. Andrew Campbell
          </h1>
          <div className="text-lg text-[#8C4A57] italic mb-6">Director, Global Leader Solutions</div>

          <div className="space-y-5 text-[#6B5E62] leading-relaxed">
            <p>Dr. Andrew Campbell is the esteemed Director of Global Leader Solutions.</p>
            <p>
              Along with decades of experience as a senior military officer, Dr. Campbell is a recognized
              national and international speaker on character-based leadership, NeuroLeadership, leading
              change, toxic leadership, and organizational conflict management and resolution. He is
              widely published in national and international journals.
            </p>
            <p>
              Dr. Campbell's accolades include being a Global Goodwill Ambassador, a 2024 Marquis Who's
              Who of America inductee, and a TEDx Speaker (2023) on{" "}
              <em>Navigating Toxic Organizational Environments — Healing Through Forgiveness</em>.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* EDUCATION & CREDENTIALS */}
    <section className="bg-[#FAF4F5] border-y border-[#E8DCDD]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Background</div>
        <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-8">
          Education and credentials
        </h2>
        <ul className="space-y-4" data-testid="faculty-credentials-list">
          {CREDENTIALS.map((c, i) => (
            <li key={i} data-testid={`credential-${i}`} className="flex gap-4 items-start">
              <span className="mt-1 flex-shrink-0 w-8 h-8 rounded-sm bg-white text-[#8C4A57] font-serif font-semibold flex items-center justify-center text-sm border border-[#E8DCDD]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[#2D2628] leading-relaxed font-serif text-lg">{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* SPEAKER / AUTHOR / THOUGHT LEADER */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Voice</div>
          <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-6">
            Speaker, Author, and Thought Leader
          </h2>
          <p className="text-[#6B5E62] leading-relaxed mb-8">
            Dr. Campbell is an internationally recognized speaker and author on leadership, emotional
            intelligence, and conflict resolution. His work focuses on helping leaders manage difficult
            conversations, lead through change, and strengthen organizational relationships.
          </p>
          <h3 className="text-xl font-serif font-semibold text-[#2D2628] mb-4">Published works</h3>
          <ul className="space-y-3" data-testid="faculty-books-list">
            {BOOKS.map((b, i) => (
              <li key={i} data-testid={`book-${i}`}
                className="border-l-2 border-[#D9C5C9] pl-5 py-1">
                <div className="font-serif text-lg italic text-[#2D2628] leading-snug">{b.title}</div>
                <div className="text-xs uppercase tracking-widest text-[#6B5E62] mt-1">{b.year}</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5 grid grid-cols-1 gap-4">
          <img src={SEMINAR_1} alt="Presenter at seminar on peace development" data-testid="faculty-image-seminar"
            className="w-full h-auto rounded-sm shadow-md object-cover" />
          <img src={FRAMEWORK} alt="Presenter at strategic framework display" data-testid="faculty-image-framework"
            className="w-full h-auto rounded-sm shadow-md object-cover" />
        </div>
      </div>
    </section>

    {/* KEYNOTE SPEAKER */}
    <section className="bg-[#FAF4F5] border-t border-[#E8DCDD]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Global Reach</div>
        <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-4">Keynote speaker</h2>
        <p className="text-[#6B5E62] leading-relaxed max-w-2xl mb-12">
          A selection of moments from Dr. Campbell's keynote addresses on leadership,
          conflict resolution, and character-based leadership at events worldwide.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4" data-testid="keynote-photos-grid">
          {KEYNOTE_PHOTOS.map((k, i) => {
            const isLogo = /logo|chapter|society|association|accredited|disc|marquis|listed/i.test(k.alt);
            return (
              <figure key={i} data-testid={`keynote-photo-${i}`}
                className="bg-white border border-[#E8DCDD] rounded-sm overflow-hidden hover:border-[#8C4A57] transition-colors duration-200 flex flex-col">
                <div className="h-56 sm:h-64 flex items-center justify-center">
                  <img src={k.src} alt={k.alt}
                    className={isLogo ? "max-w-[80%] max-h-[80%] object-contain p-4" : "w-full h-full object-cover"} />
                </div>
                {k.caption && (
                  <figcaption className="px-3 py-2 border-t border-[#E8DCDD] bg-[#FAF4F5] text-[11px] tracking-[0.14em] uppercase text-[#6E3944] text-center">
                    {k.caption}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
      <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-4">
        Bring Dr. Campbell to your next event
      </h2>
      <p className="text-[#6B5E62] max-w-xl mx-auto mb-8 leading-relaxed">
        Available for keynotes, executive workshops, and cohort masterclasses on character-based
        leadership, emotional intelligence, and organizational conflict resolution.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <a href="/contact" data-testid="faculty-book-speaker" className="btn-primary">
          Book a Speaker
        </a>
        <a href="/media" data-testid="faculty-media-link" className="btn-outline">
          See Recent Talks
        </a>
      </div>
    </section>
  </div>
  );
};

export default Faculty;
